import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogoMark } from '../components/Logo';
import './site.css';

const YEAR = new Date().getFullYear();

/**
 * Header and footer for the public pages.
 *
 * Deliberately separate from the admin Layout: this is the marketing site and
 * has no session, no sidebar and no API calls behind it.
 */
const SiteShell = ({ children }) => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  /* The header condenses and lifts once the page moves under it. */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToId = useCallback(id => {
    const el = document.getElementById(id);
    if (!el) return false;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }, []);

  /*
   * Arriving from another page (/privacy → /#about) lands with the hash
   * already set, so the scroll happens after paint rather than on click.
   */
  useEffect(() => {
    if (pathname !== '/' || !hash) return undefined;
    const id = hash.slice(1);
    const timer = setTimeout(() => scrollToId(id), 60);
    return () => clearTimeout(timer);
  }, [pathname, hash, scrollToId]);

  /** Glides to the section when we are already on the landing page. */
  const onAnchor = (event, id) => {
    if (pathname !== '/') return; // let the browser navigate, then the effect scrolls
    event.preventDefault();
    if (scrollToId(id)) window.history.replaceState(null, '', `/#${id}`);
  };

  /** The logo takes you back to the top — gliding, not jumping. */
  const onBrand = event => {
    event.preventDefault();
    if (pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.history.replaceState(null, '', '/');
    } else {
      navigate('/');
    }
  };

  const anchor = (id, label) => (
    <a href={`/#${id}`} onClick={e => onAnchor(e, id)}>
      {label}
    </a>
  );

  return (
  <div className="site">
    <header className={`site-nav ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="site-wrap site-nav-inner">
        {/* The real brand mark, not a stand-in letter — same inline SVG the
            console uses, so the two never drift apart. */}
        <Link to="/" className="site-brand" onClick={onBrand}>
          <LogoMark size={34} />
          <span>
            Tarpex<span className="site-brand-dot">.live</span>
          </span>
        </Link>

        <nav className="site-links">
          {anchor('how', 'How it works')}
          {anchor('categories', 'What we trade')}
          {anchor('sellers', 'For sellers')}
          {anchor('buyers', 'For buyers')}
          {anchor('about', 'About us')}
          <Link to="/privacy">Privacy</Link>
        </nav>

      </div>
    </header>

    <main>{children}</main>

    <footer className="site-footer">
      <div className="site-wrap site-footer-grid">
        <div>
          <div className="site-brand site-brand-light">
            <LogoMark size={34} />
            <span>
              Tarpex<span className="site-brand-dot">.live</span>
            </span>
          </div>
          <p className="site-footer-note">
            India's B2B tarpaulin supply chain — manufacturers, distributors and dealers trading
            on one platform.
          </p>
        </div>

        <div>
          <h4>Platform</h4>
          {anchor('how', 'How it works')}
          {anchor('categories', 'What we trade')}
          {anchor('sellers', 'For sellers')}
          {anchor('buyers', 'For buyers')}
          {anchor('about', 'About us')}
        </div>

        <div>
          <h4>Legal</h4>
          <Link to="/privacy">Privacy policy</Link>
          <Link to="/delete-account">Delete your account</Link>
        </div>

        <div>
          <h4>Contact</h4>
          <a href="mailto:support@tarpex.live">support@tarpex.live</a>
          <Link to="/admin/loginadmin">Admin console</Link>
        </div>
      </div>

      <div className="site-wrap site-copyright">
        © {YEAR} Tarpex.live · Operated by Stackerio
      </div>
    </footer>

    {/* Appears once you are past the fold, and glides back rather than jumping. */}
    <button
      type="button"
      aria-label="Back to top"
      className={`site-totop ${scrolled ? 'show' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      ↑
    </button>
  </div>
  );
};

export default SiteShell;
